import Regex from '@constants/Regex';
import { useState } from 'react';
import { Controller } from 'react-hook-form';
import {
    Box,
    FormGroup,
    FormHelperText,
    FormLabel,
    TextField,
    Typography,
    styled,
    useTheme
} from '@mui/material';
import { composeRules2, getValidationRules } from '@utils/composeRules';
import { BaseInputProps } from '@components/InputFieldControlled/InputText.types';
import { sanitizeHtml } from '@helper/string.utils';
import { FlexRow } from '@components/FlexRow';

const StyledContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
}));

const CharacterCountContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: theme.spacing(0.5),
}));

const CharacterCount = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.secondary,
    fontSize: 12,
    marginLeft: 'auto',
    textAlign: 'right',
}));

const OptionalText = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.disabled,
    fontSize: 12,
    fontWeight: 'normal',
}));

const InputFieldControlled = (props: BaseInputProps) => {
    const theme = useTheme();

    let rules = composeRules2(props);
    
    if (props.keyboardType === 'email-address') {
        rules = {
            ...rules,
            pattern: (value: string) => {
                // bypassed other than + for upercut email
                const upercutEmailRegex = /^(?!.*[&='<>])(?!.*\.{2})[\w+-]+(?:\.[\w+-]+)*$/;
                if (Regex.EMAIL_REGEX.test(value)) {
                    return upercutEmailRegex.test(value.split('@')[0]) || 'Veuillez entrer un email valide';
                } else {
                    return false;
                }
            },
        };
    }
    
    if (props.maxLength) {
        rules = {
            ...rules,
            maxLength: (value: string) =>
                (value || '').length <= props.maxLength || `Veuillez entrer moins de ${props.maxLength} caractères`,
        };
    }

    const isEmail = props.keyboardType === 'email-address';
    const isNumeric = props.keyboardType === 'number-pad' || props.keyboardType === 'numeric';

    const handleTextChange = (text: string, onChange: (value: string) => void) => {
        if (isNumeric) {
            // allow number or dot and comma
            text = text.replace(/[^0-9.,]/g, '');
            // comma or dot only once
            text = text.replace(/(,|\.)(?=.*[,.])/g, '');
            // remove first zero
            text = text.replace(/^0+(?=\d)/g, '');
        }
        
        let _text = text;
        if (isEmail) {
            _text = text.toLowerCase();
        }
        
        onChange(_text);
        props.onChange?.(_text);
    };

    const containerStyle = {
        ...(props.fullWidth && { maxWidth: '100%' }),
        ...props.containerStyle,
    };

    return (
        <StyledContainer sx={containerStyle}>
            <Controller
                control={props.control}
                name={props.name}
                defaultValue={props.defaultValue ?? null}
                rules={{
                    validate: getValidationRules(rules ?? {}),
                    required: props.required,
                }}
                render={({ field: { ref, name, value, onChange }, fieldState: { error } }) => {
                    const errorTextStyle = isEmail ? { marginTop: '2rem' } : {};
                    
                    return (
                        <FormGroup>
                            {props.label && (
                                <FlexRow>
                                    <FormLabel>
                                        {props.label}
                                        {props.optional && !props.viewOnly && (
                                            <OptionalText>
                                                {' (facultatif)'}
                                            </OptionalText>
                                        )}
                                    </FormLabel>
                                    {props.endAdornment && (
                                        <Box sx={{ marginBottom: 0.5 }}>
                                            {props.endAdornment}
                                        </Box>
                                    )}
                                </FlexRow>
                            )}

                            {props.helpText && !error && (
                                <FormHelperText>{props.helpText}</FormHelperText>
                            )}

                            {props.viewOnly ? (
                                <Typography
                                    variant="body2"
                                    sx={{ color: theme.palette.text.secondary }}
                                >
                                    {props.viewText ? props.viewText : value ? value : '-'}
                                </Typography>
                            ) : (
                                <TextField
                                    fullWidth
                                    inputRef={ref}
                                    id={name}
                                    value={value ?? ''}
                                    placeholder={props.placeholder}
                                    disabled={props.disabled}
                                    required={props.required}
                                    error={!!error}
                                    multiline={props.multiline}
                                    rows={props.multiline ? props.numberOfLines || 1 : undefined}
                                    autoComplete={isEmail ? 'off' : undefined}
                                    type={isEmail ? 'email' : isNumeric ? 'text' : props.type || 'text'}
                                    onChange={(event) => handleTextChange(event.target.value, onChange)}
                                    onKeyPress={(event) => {
                                        if (event.key === 'Enter' && props.onSubmitEditing) {
                                            props.onSubmitEditing();
                                        }
                                    }}
                                />
                            )}

                            {(error?.message || props.maxLength) && <CharacterCountContainer>
                                <Box>
                                    {error?.message && (
                                        <FormHelperText
                                            error
                                            sx={{ m: 0, ...errorTextStyle }}
                                        >
                                            {error.message}
                                        </FormHelperText>
                                    )}
                                </Box>

                                {props.maxLength && (
                                    <CharacterCount>
                                        {sanitizeHtml(value ?? '')?.length ?? 0}/{props.maxLength} caractères
                                    </CharacterCount>
                                )}
                            </CharacterCountContainer>}
                        </FormGroup>
                    );
                }}
            />
        </StyledContainer>
    );
};

export default InputFieldControlled;
